import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ChevronDown, ChevronUp, Search, Phone } from 'lucide-react';
import { fetchCollection } from '../../lib/cms';
import SEO from '../../components/SEO';

export default function FAQEn() {
  const [faqList, setFaqList] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function loadData() {
      const data = await fetchCollection('faq', 'en');
      setFaqList(data);
    }
    loadData();
  }, []);

  const toggleFaq = (index) => {
    if (openIndex === index) {
      setOpenIndex(null);
    } else {
      setOpenIndex(index);
    }
  };

  const filteredFaq = faqList.filter(
    (item) =>
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer ? item.answer.replace(/<[^>]*>?/gm, '') : '',
      },
    })),
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Frequently Asked Questions (FAQ)"
        description="Find answers to common questions regarding the Danshaker sorting machine, battery capacity, compatible bedding types, and rental options."
        path="/en/faq"
        lang="en"
        schema={faqSchema}
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      {/* Header Section */}
      <section className="mx-auto max-w-7xl px-4 pt-16 pb-12 md:px-8 md:pt-24">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
            <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Got questions? Find answers here</span>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Here you will find answers to the most common questions about the Danshaker sorting machine, operation, and maintenance.
          </p>

          {/* Search Bar */}
          <div className="relative max-w-lg mx-auto pt-4">
            <Search className="absolute left-4 top-7 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search in questions and answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white py-3.5 pl-12 pr-4 text-base shadow-sm focus:border-[#0E67A2] focus:outline-none focus:ring-2 focus:ring-[#0E67A2]/20"
            />
          </div>
        </div>
      </section>

      {/* Accordion FAQ List */}
      <section className="mx-auto max-w-4xl px-4 pb-20 md:px-8">
        <div className="space-y-4">
          {filteredFaq.length > 0 ? (
            filteredFaq.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={index}
                  className={`overflow-hidden rounded-2xl border bg-white transition duration-200 ${
                    isOpen ? 'border-[#0E67A2] shadow-md' : 'border-slate-200 shadow-sm hover:border-slate-300'
                  }`}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="flex w-full items-center justify-between p-6 text-left focus:outline-none cursor-pointer"
                  >
                    <span className="text-lg font-bold text-slate-900 pr-4">{item.question}</span>
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition ${
                        isOpen ? 'bg-[#0E67A2] text-white' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 bg-slate-50/50 p-6 pt-4 text-slate-700 leading-relaxed text-base">
                      <div dangerouslySetInnerHTML={{ __html: item.answer }} />
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 p-8">
              <p className="text-slate-500 font-medium">No questions matched your search.</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-3 text-sm font-bold text-[#0E67A2] hover:underline"
              >
                Clear search filter
              </button>
            </div>
          )}
        </div>

        {/* Still have questions? Callout */}
        <div className="mt-16 rounded-3xl bg-slate-900 p-8 md:p-10 text-white text-center shadow-xl space-y-4">
          <h2 className="text-2xl font-bold">Did not find the answer you were looking for?</h2>
          <p className="text-slate-300 max-w-xl mx-auto text-sm md:text-base">
            You are always welcome to call Rasmus directly or send an inquiry. We are ready to help.
          </p>
          <div className="pt-2 flex flex-wrap justify-center gap-4">
            <a
              href="tel:25303589"
              className="inline-flex items-center gap-2 rounded-xl bg-[#0E67A2] px-6 py-3 font-bold text-white transition hover:bg-[#0E67A2]/90 shadow-md"
            >
              <Phone className="h-4 w-4" />
              Call +45 2530 3589
            </a>
            <Link
              to="/en/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-6 py-3 font-bold text-white transition hover:bg-slate-700"
            >
              Contact form
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
